import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import {REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY} from './AlphaFoldTabContent';
import {requestRun} from './AlphaFoldTabUtils';

function RedoPredictionModal({
  proteinId,
  onRequestClose,
}: {
  proteinId: string;
  onRequestClose: () => void;
}): JSX.Element {
  const modalProps = {title: 'Redo structure prediction', onRequestClose, medium: true};
  const queryResult = useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(
    REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY
  );
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading user details. Please refresh the page.');
    onRequestClose();
    return <></>;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return (
      <Modal {...modalProps}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  } else {
    const quota = BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT;
    const remaining = queryResult.data.remainingAlphafoldExecutions;
    const jobQuotaText = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC
      ? `You have ${remaining} of ${quota} requests remaining this year.`
      : `You have ${remaining} of ${quota} requests remaining this month across ${queryResult.data.currentTenant.displayName}.`;

    return (
      <Modal
        {...modalProps}
        buttons={[
          {
            text: 'Predict 3D structure',
            disabled: queryResult.data.remainingAlphafoldExecutions <= 0,
            submit: true,
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              requestRun(proteinId, queryResult.data);
              trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_CONFIRM_REDO_RUN);
              onRequestClose();
            },
          },
        ]}
      >
        <>
          <p className={css(styles.text)}>
            {jobQuotaText} Redoing structure prediction will use up one of those requests.
          </p>
          <p className={css(styles.text)}>
            You will still be able to view the current structure while the new one is being generated.
          </p>
          <p className={css(styles.text)}>Would you like to proceed?</p>
        </>
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  text: FONT_PROXIMA_13,
});

export default RedoPredictionModal;
